import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type { AdvancedFilterData } from "@redotech/redo-model/views/views";
import {
  AdvancedTableFilter,
  ArrayStringTableFilter,
  DateTableFilter,
  ExistsTableFilter,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import { ArrayStringFilterGroup } from "@redotech/redo-web/advanced-filters/components/array-to-string-filter";
import { DateFilterGroup } from "@redotech/redo-web/advanced-filters/components/date-filter";
import { ExistsFilterGroup } from "@redotech/redo-web/advanced-filters/components/exists-filter";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { deepCopyObject } from "@redotech/util/object";
import { useLiveQuery } from "@tanstack/react-db";
import { memo, useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import {
  useAdvancedFilters,
  useHasActiveFilters,
  useResetFilters,
} from "./filters-context";
import { getConversationAdvancedFilters } from "./get-conversation-advanced-filters";

function shouldOpenOnRender(
  filters: AdvancedFilterData[],
  filter: AdvancedFilterData,
  filterJustAdded: boolean,
): boolean {
  if (!filterJustAdded || filters[filters.length - 1] !== filter) {
    return false;
  }

  if (!("value" in filter)) {
    return false;
  }

  const value = filter.value;
  return (
    value === null ||
    value === undefined ||
    (Array.isArray(value) && value.length === 0) ||
    value === ""
  );
}

/**
 * Convert AdvancedFilterData to AdvancedTableFilter by finding the matching
 * filter definition and copying in the current filter data.
 */
function dataToTableFilter(
  data: AdvancedFilterData,
  allFilters: AdvancedTableFilter[],
): AdvancedTableFilter | null {
  const matchingFilter = allFilters.find((f) => f.data.name === data.name);
  if (!matchingFilter) {
    return null;
  }
  const filterCopy = deepCopyObject(matchingFilter);
  filterCopy.data = data;
  return filterCopy;
}

/**
 * Renders the appropriate filter component based on filter type.
 * This follows the same pattern as table2.tsx AdvancedFilterComponent.
 */
const FilterRenderer = memo(function FilterRenderer({
  filter,
  setFilter,
  removeFilter,
  openOnRender,
  allFilters,
}: {
  filter: AdvancedTableFilter;
  setFilter: (filter: AdvancedTableFilter) => void;
  removeFilter: () => void;
  openOnRender: boolean;
  allFilters: AdvancedTableFilter[];
}) {
  switch (filter.type) {
    case AdvancedFilterType.DATE:
      return (
        <DateFilterGroup
          filter={filter as DateTableFilter}
          openOnRender={openOnRender}
          removeFilter={removeFilter}
          setFilter={setFilter as (filter: DateTableFilter) => void}
        />
      );
    case AdvancedFilterType.ARRAY_TO_STRING:
      return (
        <ArrayStringFilterGroup
          filter={filter as ArrayStringTableFilter}
          filters={allFilters}
          openOnRender={openOnRender}
          removeFilter={removeFilter}
          setFilter={setFilter as (filter: ArrayStringTableFilter) => void}
        />
      );
    case AdvancedFilterType.EXISTS:
      return (
        <ExistsFilterGroup
          filter={filter as ExistsTableFilter}
          openOnRender={openOnRender}
          removeFilter={removeFilter}
          setFilter={setFilter as (filter: ExistsTableFilter) => void}
        />
      );
    default:
      console.warn("Unknown filter type:", filter.type);
      return null;
  }
});

export const ActiveConversationFilters = memo(
  function ActiveConversationFilters() {
    const { filters: advancedFilters, setFilters: setAdvancedFilters } =
      useAdvancedFilters();
    const hasActiveFilters = useHasActiveFilters();
    const resetFilters = useResetFilters();
    const [searchParams] = useSearchParams();
    const isOnDefaultView = !!searchParams.get("defaultView");
    const collections = useGlobalConversationCollections();
    const platformRpc = useRequiredContext(RedoPlatformAuthRpcClientContext);
    const effectApi = useRequiredContext(EffectApiClientContext);
    const { data: tags } = useLiveQuery(
      (q) => q.from({ tag: collections.tags }),
      [],
    );

    // Get all available filter definitions
    const allFilterDefinitions = useMemo(
      () => getConversationAdvancedFilters({ effectApi, platformRpc, tags }),
      [effectApi, platformRpc, tags],
    );

    const [previousFilterCount, setPreviousFilterCount] = useState(
      advancedFilters.length,
    );
    const filterJustAdded = advancedFilters.length > previousFilterCount;

    useMemo(() => {
      if (advancedFilters.length !== previousFilterCount) {
        setPreviousFilterCount(advancedFilters.length);
      }
    }, [advancedFilters.length, previousFilterCount]);

    const handleSetFilter = useCallback(
      (oldFilter: AdvancedFilterData, newTableFilter: AdvancedTableFilter) => {
        setAdvancedFilters((prev) =>
          prev.map((f) => (f === oldFilter ? newTableFilter.data : f)),
        );
      },
      [setAdvancedFilters],
    );

    const handleRemoveFilter = useCallback(
      (filter: AdvancedFilterData) => {
        setAdvancedFilters((prev) => prev.filter((f) => f !== filter));
      },
      [setAdvancedFilters],
    );

    // Convert AdvancedFilterData[] to AdvancedTableFilter[]
    const displayedFilters: {
      data: AdvancedFilterData;
      tableFilter: AdvancedTableFilter;
    }[] = useMemo(() => {
      return advancedFilters
        .map((data) => {
          const tableFilter = dataToTableFilter(data, allFilterDefinitions);
          if (!tableFilter) {
            return null;
          }
          return { data, tableFilter };
        })
        .filter(
          (
            item,
          ): item is {
            data: AdvancedFilterData;
            tableFilter: AdvancedTableFilter;
          } => item !== null,
        );
    }, [advancedFilters, allFilterDefinitions]);

    return (
      <Flex align="center" className="min-h-7" gap="sm" wrap="wrap">
        {displayedFilters.map(({ data, tableFilter }, index) => {
          const openOnRender = shouldOpenOnRender(
            advancedFilters,
            data,
            filterJustAdded,
          );
          return (
            <FilterRenderer
              allFilters={allFilterDefinitions}
              filter={tableFilter}
              key={`${data.name}-${index}`}
              openOnRender={openOnRender}
              removeFilter={() => handleRemoveFilter(data)}
              setFilter={(newFilter) => handleSetFilter(data, newFilter)}
            />
          );
        })}
        {hasActiveFilters && !isOnDefaultView && (
          <RedoButton
            hierarchy="tertiary"
            onClick={resetFilters}
            size="xs"
            text="Reset filters"
          />
        )}
      </Flex>
    );
  },
);
